import { getCountries } from '@choco-admin/utils';
import {
  Dropdown,
  DropdownTrigger,
  DropdownMenu,
  DropdownItem,
  Button,
} from '@heroui/react';
import type { SharedSelection } from '@heroui/react';
import React from 'react';
import { Controller, Control, FieldValues, Path } from 'react-hook-form';

export interface CountryDropdownProps {
  /** Name attribute for form submission */
  name?: string;
  /** Current selected country value */
  value?: string;
  /** Default selected country */
  defaultSelectedKeys?: string[];
  /** Placeholder text */
  placeholder?: string;
  /** Whether the dropdown is required */
  isRequired?: boolean;
  /** Whether the dropdown is disabled */
  isDisabled?: boolean;
  /** Whether the dropdown is readonly */
  isReadOnly?: boolean;
  /** Input variant style */
  variant?: 'flat' | 'bordered' | 'underlined' | 'faded';
  /** Size of the dropdown */
  size?: 'sm' | 'md' | 'lg';
  /** Error message */
  errorMessage?: string;
  /** Custom className */
  className?: string;
  /** Custom classNames for styling */
  classNames?: {
    trigger?: string;
    value?: string;
    [key: string]: string | undefined;
  };
  /** Change handler */
  onSelectionChange?: (keys: SharedSelection) => void;
}

export interface CountryFormFieldProps<TFieldValues extends FieldValues> {
  /** Field name in the form */
  name: Path<TFieldValues>;
  /** React Hook Form control */
  control: Control<TFieldValues>;
  /** Field label */
  label?: string;
  /** Whether the field is required */
  isRequired?: boolean;
  /** Whether the field is disabled */
  isDisabled?: boolean;
  /** Whether the field is readonly */
  isReadOnly?: boolean;
  /** Custom className */
  className?: string;
}

export const CountryDropdown: React.FC<CountryDropdownProps> = ({
  name = 'country',
  value,
  defaultSelectedKeys,
  placeholder = 'Select a country',
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  variant = 'bordered',
  size = 'md',
  errorMessage,
  className,
  classNames,
  onSelectionChange,
}) => {
  return (
    <Dropdown>
      <DropdownTrigger>
        <Button
          variant="bordered"
          className="w-full justify-start bg-default-50 border-default-200"
          isDisabled={isDisabled || isReadOnly}
        >
          {value || placeholder}
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label="Country options"
        selectionMode="single"
        selectedKeys={value ? new Set([value]) : new Set()}
        onSelectionChange={onSelectionChange}
        className="max-h-60 overflow-y-auto"
        classNames={{
          base: 'bg-white dark:bg-gray-800 border border-default-200',
          list: 'bg-white dark:bg-gray-800',
        }}
      >
        {getCountries().map((country) => (
          <DropdownItem
            key={country}
            className="text-foreground hover:bg-default-100"
          >
            {country}
          </DropdownItem>
        ))}
      </DropdownMenu>
    </Dropdown>
  );
};

/**
 * CountryFormField - React Hook Form integrated country dropdown
 * Use this component when working with React Hook Form
 */
export const CountryFormField = <TFieldValues extends FieldValues>({
  name,
  control,
  label,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  className,
}: CountryFormFieldProps<TFieldValues>) => {
  return (
    <div className={`space-y-2 ${className || ''}`}>
      {label && (
        <label className="text-sm font-medium text-foreground">
          {label}
          {isRequired && <span className="text-danger ml-1">*</span>}
        </label>
      )}
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <div>
            <Dropdown>
              <DropdownTrigger>
                <Button
                  variant="bordered"
                  className="w-full justify-start bg-default-50 border-default-200"
                  aria-label={`Select ${label || 'country'}`}
                >
                  {field.value || 'Select a country'}
                </Button>
              </DropdownTrigger>
              <DropdownMenu
                aria-label={`${label || 'Country'} options`}
                selectionMode="single"
                selectedKeys={field.value ? new Set([field.value]) : new Set()}
                onSelectionChange={(keys) => {
                  const selectedKey = Array.from(keys)[0] as string;
                  field.onChange(selectedKey || '');
                }}
                className="max-h-60 overflow-y-auto"
                classNames={{
                  base: 'bg-white dark:bg-gray-800 border border-default-200 p-2',
                  list: 'bg-white dark:bg-gray-800 p-1t 0',
                }}
              >
                {getCountries().map((country) => (
                  <DropdownItem
                    key={country}
                    className="text-foreground hover:bg-default-100"
                  >
                    {country}
                  </DropdownItem>
                ))}
              </DropdownMenu>
            </Dropdown>
            {error && (
              <span className="text-danger text-xs mt-1 block">
                {error.message}
              </span>
            )}
          </div>
        )}
      />
    </div>
  );
};
